import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { HardDriveDownload, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  formatBytes,
  listStored,
  offlineSupported,
  removeAll,
  type StoredTrack,
} from '@/lib/offline-audio';

/**
 * Lets someone see what the app has put on their phone, and take it back off.
 *
 * A meditation is two tracks and tens of megabytes; without somewhere to look,
 * downloads accumulate invisibly until the phone complains. Hidden entirely on
 * the web, where nothing is ever stored.
 */
export function OfflineDownloadsSection() {
  const [tracks, setTracks] = useState<StoredTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    setTracks(await listStored());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!offlineSupported()) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  if (!offlineSupported() || loading) return null;

  const total = tracks.reduce((sum, t) => sum + t.bytes, 0);

  const handleClear = async () => {
    setClearing(true);
    try {
      await removeAll();
      await refresh();
      toast({
        title: 'Downloads verwijderd',
        description: 'De meditaties worden weer gestreamd wanneer je ze afspeelt.',
      });
    } catch (err) {
      console.error('Clearing downloads failed:', err);
      toast({ variant: 'destructive', title: 'Verwijderen mislukt' });
    } finally {
      setClearing(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <div className="flex flex-col items-center gap-2 text-center">
        <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <HardDriveDownload className="h-4 w-4" />
          Offline meditaties
        </h4>

        {tracks.length === 0 ? (
          <p className="text-xs text-muted-foreground max-w-md">
            Je hebt nog niets opgeslagen. Druk bij een meditatie op &ldquo;Offline
            opslaan&rdquo; om hem zonder internet te kunnen luisteren.
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground max-w-md">
              {tracks.length} {tracks.length === 1 ? 'bestand' : 'bestanden'} op dit
              apparaat, samen {formatBytes(total)}. Deze meditaties spelen zonder
              internet en kosten geen data.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Maak ruimte vrij
            </Button>
          </>
        )}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(next) => !clearing && setConfirmOpen(next)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle downloads verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit haalt {formatBytes(total)} van je apparaat. Je kunt de
              meditaties gewoon blijven luisteren, ze worden dan weer gestreamd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              disabled={clearing}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {clearing ? 'Bezig...' : 'Verwijderen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
